import React from 'react';
import { Modal, Button, Text, Group, Box, Stack, ThemeIcon, Container } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@/store/Store';
import { IconCheck, IconEdit, IconPlus, IconX } from '@tabler/icons-react';

interface SuccessPopupProps {
  opened: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  onResetForm: () => void;
}

// Helper function to create gradient from user color
const createGradientFromColor = (color: string) => {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Create lighter and darker variations
  const lightR = Math.min(255, r + 40);
  const lightG = Math.min(255, g + 40);
  const lightB = Math.min(255, b + 40);
  
  const darkR = Math.max(0, r - 30);
  const darkG = Math.max(0, g - 30);
  const darkB = Math.max(0, b - 30);
  
  return `linear-gradient(135deg, rgb(${lightR}, ${lightG}, ${lightB}) 0%, rgb(${darkR}, ${darkG}, ${darkB}) 100%)`;
};

export function SuccessPopup({ 
  opened, 
  onClose, 
  productId, 
  productName, 
  onResetForm 
}: SuccessPopupProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const color: string = useStore((state) => state.userProfile.qrCodeColor);

  const handleEditProduct = () => {
    navigate(`/editproduct?id=${productId}`);
    onClose();
  };

  const handleCreateNew = () => {
    onResetForm();
    onClose();
  };

  const handleClose = () => {
    onResetForm();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size="md"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      styles={{
        content: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
        },
        header: {
          display: 'none',
        },
        body: {
          padding: 0,
        },
      }}
    >
      <Box
        style={{
          background: createGradientFromColor(color),
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <Box
          style={{
            position: 'absolute',
            top: '-30%',
            right: '-30%',
            width: '150%',
            height: '150%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        
        {/* Close button */}
        <Button
          variant="subtle"
          color="white"
          size="sm"
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
            borderRadius: '50%',
            width: 28,
            height: 28,
            minHeight: 28,
            padding: 0,
            background: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }}
        >
          <IconX size={14} />
        </Button>

        <Container py={24} px={24}>
          <Stack align="center" gap="lg">
            {/* Success Icon */}
            <ThemeIcon
              size={60}
              radius="xl"
              style={{
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.4)',
              }}
            >
              <IconCheck size={30} color="white" />
            </ThemeIcon>

            {/* Success Message */}
            <Stack align="center" gap="xs">
              <Text
                size="lg"
                fw={700}
                c="white"
                ta="center"
                style={{
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                }}
              >
                {t('editProduct.successTitle')}
              </Text>
              
              <Text
                size="md"
                c="rgba(255, 255, 255, 0.9)"
                ta="center"
                style={{
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  maxWidth: '300px',
                }}
              >
                {t('editProduct.productCreated', { name: productName })}
              </Text>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Action Buttons Section */}
      <Box p={24} style={{ background: 'var(--mantine-color-body)' }}>
        <Stack gap="sm">
          <Text
            size="xs"
            c="dimmed"
            ta="center"
            mb="xs"
          >
            {t('editProduct.whatWouldYouLikeToDoNext')}
          </Text>
          
          <Group justify="center" gap="sm">
            <Button
              variant="outline"
              color={color}
              onClick={handleEditProduct}
              size="md"
              leftSection={<IconEdit size={16} />}
              style={{
                borderRadius: '10px',
                fontWeight: 600,
                minWidth: '140px',
                borderWidth: '2px',
                transition: 'all 0.2s ease',
              }}
              styles={{
                root: {
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
                  },
                },
              }}
            >
              {t('editProduct.editProduct')}
            </Button>
            
            <Button
              color={color}
              onClick={handleCreateNew}
              size="md"
              leftSection={<IconPlus size={16} />}
              style={{
                borderRadius: '10px',
                fontWeight: 600,
                minWidth: '140px',
                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                transition: 'all 0.2s ease',
              }}
              styles={{
                root: {
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
                  },
                },
              }}
            >
              {t('editProduct.createNewProduct')}
            </Button>
          </Group>
        </Stack>
      </Box>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(180deg);
          }
        }
      `}</style>
    </Modal>
  );
}
